import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { HadithController } from '@components';

import { AiSvg } from '@components/AiSvg';
import { ResultsStats } from '@components/ResultsStats';
import { SeoHead } from '@components/SeoHead';
import { MainModal } from '@components/modal/Modal';
import HadithResultItem from '@components/results/items/HadithResultItem';
import { getBookAndIdText } from '@components/results/items/helpers';
import { SearchInput } from '@components/search/SearchBarAutosuggest/styled';
import HadithSkeleton from '@components/skeletons/HadithSkeleton';
import MySnackbar, { MySnackbarProps } from '@components/snackbar/Snackbar';
import Feedback from '@components/views/MainSearchViews/Feedback';

import { numNeighbors } from '@constants/filters';
import { useContextualRouting } from '@hooks';
import useTranslation from '@hooks/useTranslation';
import { PageLayout } from '@layout';
import { Search as SearchIcon } from '@mui/icons-material';
import { Clear as ClearIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    ButtonBase,
    Grid,
    IconButton,
    InputAdornment,
    InputBaseProps,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import { SemanticSearchHadith } from 'shared/interfaces/hadith';
import { colors } from 'theme';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

function Suggestion({
    suggestion,
    onClick,
}: {
    suggestion: string;
    onClick: (x: string) => void;
}) {
    return (
        <ButtonBase
            component="div"
            sx={{
                borderRadius: '10px',
                borderStyle: 'solid',
                borderWidth: '1px',
                borderColor: colors.secondary,
                height: '72px',
                maxWidth: '288px',
                textAlign: 'center',
                width: '100%',
                '&:hover': {
                    opacity: 0.8,
                },
                // https://gist.github.com/alvinthen/a43fba3d9763e69d2c513550bcb419ec
                boxShadow: `0px 2px 4px -1px rgba(0, 0, 0, 0.2),
                    0px 4px 5px 0px rgba(0, 0, 0, 0.14),
                    0px 1px 10px 0px rgba(0, 0, 0, 0.12)`,
            }}
            onClick={() => onClick(suggestion)}
        >
            <Typography variant="body2" color="secondary">
                {suggestion}
            </Typography>
        </ButtonBase>
    );
}

function SemanticSuggestions({
    onClick,
    isMobile,
}: {
    onClick: (x: string) => void;
    isMobile: boolean;
}) {
    const mobileSize = 50;
    const desktopSize = 90;
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Grid
                container
                columnGap={2}
                rowGap={2}
                justifyContent="center"
                alignItems="center"
                maxWidth="md"
                sx={{
                    my: 2,
                }}
            >
                <Grid
                    item
                    xs={12}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <AiSvg
                        width={isMobile ? mobileSize : desktopSize}
                        height={isMobile ? mobileSize : desktopSize}
                        color={colors.secondary}
                    />
                    <Typography variant="h5" component="h1" textAlign="center">
                        أمثلة للبحث بالمعنى
                    </Typography>
                </Grid>

                <Grid
                    item
                    xs={5}
                    zeroMinWidth
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Suggestion suggestion="حكم لبس الحرير" onClick={onClick} />
                </Grid>
                <Grid
                    item
                    xs={5}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Suggestion
                        suggestion="من قتل أبو جهل يوم بدر؟"
                        onClick={onClick}
                    />
                </Grid>
                <Grid
                    item
                    xs={5}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Suggestion suggestion="كيفية التيمم" onClick={onClick} />
                </Grid>
                <Grid
                    item
                    xs={5}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Suggestion
                        suggestion="من قاد المسلمين في غزوة مؤتة؟"
                        onClick={onClick}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

function onSubmit(search: string): Promise<SemanticSearchHadith[]> {
    const queryNoTashkeel = search.replaceAll(/[\u064b-\u0652]/g, '');

    fetch('/api/search_analytics', {
        method: 'POST',
        body: JSON.stringify({
            search: search,
            type: 'semantic',
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return fetch(`/api/semantic?search=${queryNoTashkeel}`)
        .then((res) => res.json())
        .then((json) => {
            const hits = json.data;

            const parsedHits = z
                .array(
                    z.object({
                        _source: SemanticSearchHadith,
                    }),
                )
                .parse(hits);
            return parsedHits.map((hit) => hit._source);
        });
}

const SearchPage = () => {
    const router = useRouter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { returnHref } = useContextualRouting();

    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    // null is initial / no search
    // empty array indicates no results found
    const [results, setResults] = useState<SemanticSearchHadith[] | null>(null);

    const [selectedHadith, setSelectedHadith] =
        useState<null | SemanticSearchHadith>(null);

    useEffect(() => {
        const hadithId = router.query.hadithId;
        if (!hadithId) {
            return;
        }

        const hadithFromResults = results?.find(
            (res) => res.hadith_id === hadithId,
        );
        if (hadithFromResults) {
            setSelectedHadith(hadithFromResults);
            return;
        }

        fetch(`/api/semantic-hadith?id=${hadithId}`)
            .then((res) => res.json())
            .then((json) => {
                const {
                    data: [parsed],
                } = z
                    .object({ data: SemanticSearchHadith.array().length(1) })
                    .parse(json);
                setSelectedHadith(parsed);
            });
    }, [router.query.hadithId, results]);

    const [showSnackbar, setShowSnackbar] = useState<MySnackbarProps>({
        message: '',
        show: false,
    });

    const { t } = useTranslation('library');

    const mobileInputProps: InputBaseProps['inputProps'] = isMobile
        ? {
              autoComplete: 'off',
              lang: 'ar',
              // takes only certain allowed values: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/enterkeyhint
              enterKeyHint: 'search',
          }
        : {};

    const mobilePositionProps = isMobile
        ? { position: 'fixed', left: 0, right: 0, top: 0, zIndex: 2 }
        : {};

    const resultStatsLabel = isMobile
        ? t('similar_matn')
        : t('matns_count_with_filters');

    const searchField = (
        <SearchInput
            id="ai-search-input"
            variant="outlined"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            multiline
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    setLoading(true);
                    setSearch(inputValue);
                    onSubmit(inputValue).then((results) => {
                        setResults(results);
                        setLoading(false);
                    });
                }
            }}
            size={isMobile ? 'small' : 'medium'}
            type="search"
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            rows={isMobile ? 1.2 : 1.4}
            placeholder={isMobile ? t('ai_search_mobile') : t('ai_search')}
            inputProps={mobileInputProps}
            InputProps={{
                style: {
                    textAlign: 'left',
                },

                endAdornment: (
                    <InputAdornment position="end">
                        <SearchIcon
                            fontSize="medium"
                            color="secondary"
                            aria-label="search"
                        />
                    </InputAdornment>
                ),
                startAdornment: (
                    <InputAdornment position="start">
                        {inputValue.length > 0 && (
                            <IconButton
                                onClick={() => {
                                    setInputValue('');
                                    setSearch('');
                                    setResults(null);
                                }}
                                sx={{
                                    pl: 1,
                                    mb: 0,
                                }}
                            >
                                <ClearIcon
                                    fontSize="medium"
                                    aria-label="clear"
                                    color="secondary"
                                />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
            sx={{
                pt: 0,
                outline: 'none',
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        border: '1px solid rgba(0,0,0,0.2)',
                    },
                    '&.Mui-focused fieldset': {
                        border: '1px solid rgba(0,0,0,0.2)',
                    },
                },
            }}
        />
    );

    return (
        <>
            <SeoHead />
            <PageLayout
                headerIcons={searchField}
                onLogoClick={() => {
                    setInputValue('');
                    setSearch('');
                    setResults(null);
                }}
            >
                <Paper
                    elevation={0}
                    variant={isMobile ? 'elevation' : 'outlined'}
                    sx={{
                        px: 2,
                        py: { sm: 2 },
                        borderRadius: '10px',
                        mt: { xs: 0, md: 2 },
                        minHeight: '60vh',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            ...mobilePositionProps,
                            boxShadow: {
                                xs: '0 5px 10px -2px rgba(0, 0, 0, 0.19)',
                                sm: 'none',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                py: '10px',
                            }}
                        >
                            {searchField}
                        </Box>
                    </Box>
                    <Box
                        sx={
                            search
                                ? null
                                : {
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: '100%',
                                  }
                        }
                    >
                        {search ? null : (
                            <SemanticSuggestions
                                isMobile={isMobile}
                                onClick={(x) => {
                                    setInputValue(x);
                                    setLoading(true);
                                    setSearch(x);
                                    onSubmit(x).then((results) => {
                                        setResults(results);
                                        setLoading(false);
                                    });
                                }}
                            />
                        )}
                        {search ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    mt: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                    }}
                                >
                                    <ResultsStats
                                        loading={loading}
                                        totalMatns={0}
                                        totalResults={
                                            results ? results.length : 0
                                        }
                                        resultStatsLabel={resultStatsLabel}
                                    />
                                </Box>
                                <Feedback
                                    // unmount component with a new search
                                    key={search}
                                    search={search}
                                    setShowSnackbar={setShowSnackbar}
                                    loading={loading}
                                />
                                <Alert
                                    severity="warning"
                                    sx={{
                                        paddingY: '0px',
                                        height: 'fit-content',
                                        textAlign: 'center',
                                        width: 'fit-content',
                                    }}
                                >
                                    <span>{t('semantic_disclaimer')}</span>
                                </Alert>
                            </Box>
                        ) : null}
                        {search && loading ? (
                            <Box>
                                {Array(numNeighbors)
                                    .fill(0)
                                    .map(() => (
                                        <HadithSkeleton
                                            key={uuidv4()}
                                            withActions
                                        />
                                    ))}
                            </Box>
                        ) : null}
                        {results ? (
                            <Box>
                                {results.length > 0 ? (
                                    results.map((result) => (
                                        <HadithResultItem
                                            key={uuidv4()}
                                            hadith_object={result}
                                            setShowSnackbar={setShowSnackbar}
                                        />
                                    ))
                                ) : (
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        mt={4}
                                        textAlign="center"
                                    >
                                        {t('semantic_no_results_founds')}
                                    </Typography>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                </Paper>
            </PageLayout>
            {selectedHadith && router.query.hadithId ? (
                <MainModal
                    originalHadith={selectedHadith}
                    handleClose={() => {
                        router.push(returnHref, undefined, {
                            shallow: true,
                        });
                        setSelectedHadith(null);
                    }}
                    moveTitleToRight={true}
                    title={
                        isMobile
                            ? selectedHadith
                                ? getBookAndIdText(
                                      selectedHadith.book_name,
                                      selectedHadith.number[0],
                                  )
                                : ''
                            : ''
                    }
                >
                    <Paper
                        elevation={0}
                        variant={isMobile ? 'elevation' : 'outlined'}
                        sx={{
                            border: 'none',
                            height: 'fit-content',
                        }}
                    >
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            flexDirection={'column'}
                        >
                            <Box width="100%">
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            md: 'block',
                                        },
                                    }}
                                >
                                    <HadithResultItem
                                        key={selectedHadith.hadith_id}
                                        hadith_object={selectedHadith}
                                        rightActions={[]}
                                        setShowSnackbar={setShowSnackbar}
                                    />
                                </Box>
                                <HadithController
                                    hadith={selectedHadith}
                                    setShowSnackbar={setShowSnackbar}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </MainModal>
            ) : null}
            <MySnackbar
                anchorOrigin={{
                    vertical: isMobile ? 'top' : 'bottom',
                    horizontal: isMobile ? 'center' : 'right',
                }}
                message={showSnackbar.message}
                key="top-center"
                open={showSnackbar.show}
                onClose={() => setShowSnackbar({ message: '', show: false })}
                autoHideDuration={3000}
                severity={showSnackbar.severity}
            />
        </>
    );
};

export default SearchPage;
